import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { PieChart } from 'react-native-gifted-charts';
import { PoppinsFonts, useTheme } from '../../theme';
import { formatWithCommas, fs, s, v } from '../../utils/libs';
import { CATEGORICAL_CHART_COLORS } from './chartColors';

type Props = {
  data: Array<{ label: string; value: number }>;
  selectedCategoryIndex: number | null;
  onCategoryPress: (index: number) => void;
};

const withOpacity = (hexColor: string, opacity: number) => {
  const hex = hexColor.replace('#', '');
  const alpha = Math.round(Math.max(0, Math.min(opacity, 1)) * 255)
    .toString(16)
    .padStart(2, '0');

  return `#${hex}${alpha}`;
};

const CategoryDonutChart: React.FC<Props> = ({
  data,
  selectedCategoryIndex,
  onCategoryPress,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const chartData = useMemo(
    () =>
      [...(data || [])]
        .map(item => ({
          label: String(item.label || 'Uncategorized'),
          value: Number(item.value || 0),
        }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value),
    [data],
  );

  const total = useMemo(
    () => chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData],
  );

  const pieData = useMemo(
    () =>
      chartData.map((item, index) => {
        const pct = total > 0 ? (item.value / total) * 100 : 0;
        const pctText = `${pct.toFixed(pct >= 10 ? 1 : 2)}%`;
        const color =
          CATEGORICAL_CHART_COLORS[index % CATEGORICAL_CHART_COLORS.length];
        const isDimmed =
          selectedCategoryIndex !== null && selectedCategoryIndex !== index;

        const isSelected =
          selectedCategoryIndex !== null && selectedCategoryIndex === index;

        return {
          value: item.value,
          color: isDimmed ? withOpacity(color, 0.35) : color,
          focused: isSelected,
          legendColor: color,
          text: `${item.label} — ${pctText}`,
          legendLabel: item.label,
          legendValue: item.value,
          legendPct: pctText,
          isDimmed,
        };
      }),
    [chartData, selectedCategoryIndex, total],
  );

  if (pieData.length === 0 || total <= 0) {
    return <Text style={styles.noDataText}>No data</Text>;
  }

  return (
    <View style={styles.container}>
      <PieChart
        data={pieData}
        donut
        isAnimated
        extraRadius={v(8)}
        onPress={(_: unknown, index: number) => {
          onCategoryPress?.(index);
        }}
        radius={v(92)}
        innerRadius={v(46)}
        strokeWidth={1}
        strokeColor={theme.colors.surface}
        innerCircleColor={theme.colors.surface}
        backgroundColor={theme.colors.surface}
        textColor={theme.colors.text}
        textSize={fs(10)}
        fontWeight="600"
        centerLabelComponent={() => (
          <View style={styles.centerLabel}>
            <Text style={styles.centerValue}>
              {formatWithCommas(Math.round(total), 0)}
            </Text>
            <Text style={styles.centerText}>Total</Text>
          </View>
        )}
      />

      <View style={styles.legendWrap}>
        {pieData.map((item, index) => (
          <TouchableOpacity
            key={`${item.legendLabel}-${index}`}
            onPress={() => onCategoryPress?.(index)}
            activeOpacity={0.7}
            style={[
              styles.legendItem,
              item.isDimmed && styles.legendItemDimmed,
            ]}>
            <View
              style={[
                styles.legendDot,
                { backgroundColor: String(item.legendColor) },
              ]}
            />
            <Text style={styles.legendText} numberOfLines={2}>
              {`${String(item.legendLabel)} — ${String(item.legendPct)} · ${formatWithCommas(
                Math.round(Number(item.legendValue || 0)),
                0,
              )}`}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};

export default CategoryDonutChart;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      alignItems: 'center',
      paddingTop: v(4),
    },
    centerLabel: {
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: s(84),
    },
    centerValue: {
      color: theme.colors.text,
      fontFamily: PoppinsFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
      textAlign: 'center',
    },
    centerText: {
      color: theme.colors.textSecondary,
      fontFamily: PoppinsFonts.regular,
      fontSize: fs(10),
      lineHeight: fs(13),
      textAlign: 'center',
    },
    legendWrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      width: '100%',
      paddingTop: v(4),
      paddingHorizontal: s(6),
      rowGap: v(4),
    },
    legendItem: {
      width: '48%',
      flexDirection: 'row',
      alignItems: 'flex-start',
      columnGap: s(7),
    },
    legendItemDimmed: {
      opacity: 0.35,
    },
    legendDot: {
      width: s(9),
      height: s(9),
      borderRadius: s(4.5),
      marginTop: v(3),
    },
    legendText: {
      flex: 1,
      color: theme.colors.text,
      fontFamily: PoppinsFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(15),
    },
    noDataText: {
      color: theme.colors.textSecondary,
      fontFamily: PoppinsFonts.regular,
      fontSize: fs(12),
      paddingVertical: v(24),
      textAlign: 'center',
    },
  });
